import React, { type PropsWithChildren, useRef } from 'react';
import { StyleSheet, type TextStyle, TouchableOpacity, type ViewStyle } from 'react-native';
import Animated, {
  interpolate,
  interpolateColor,
  type WithTimingConfig,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { BodyText, Icon, type IconName, theme, useHovered, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellButton = (props: Props) => {
  const { palette, label, icon, disabled = false, children, onPress } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const buttonRef = useRef(null);
  const isHovered = useHovered(buttonRef);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const hoverProgress = useDerivedValue(() => {
    return withTiming(isHovered ? 1 : 0, HOVER_TIMING_CONFIG);
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const buttonAnimatedStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        hoverProgress.value,
        HOVER_INPUT_RANGE,
        HOVER_BACKGROUND_COLORS[palette],
      ),
      opacity: interpolate(hoverProgress.value, HOVER_INPUT_RANGE, HOVER_OPACITIES[palette]),
    };
  });

  const touchableStyles = [styles.touchable, TOUCHABLE_WEB_STYLES];
  const buttonStyles = [buttonPaletteStyles[palette], buttonAnimatedStyle];
  const labelStyles = labelPaletteStyles[palette];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={buttonRef}
      accessibilityRole="button"
      accessibilityLabel={label}
      activeOpacity={0.7}
      disabled={disabled}
      hitSlop={8}
      style={touchableStyles}
      onPress={onPress}
    >
      <Animated.View style={buttonStyles}>
        {children ?? (
          <>
            {icon ? (
              <Icon name={icon} width={24} height={24} color={theme.colors.DARK_KALE} />
            ) : null}

            <BodyText sizeMatch={['18']} underline={palette === 'link'} style={labelStyles}>
              {label}
            </BodyText>
          </>
        )}
      </Animated.View>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HOVER_INPUT_RANGE = [0, 1];
const HOVER_TIMING_CONFIG: WithTimingConfig = { duration: theme.transitions.base };

const HOVER_BACKGROUND_COLORS: Record<Palette, string[]> = {
  primary: [theme.colors.OATMEAL, theme.colors.QUINOA],
  outline: [theme.colors.OPACITY.OATMEAL.TRANSPARENT, theme.colors.OPACITY.OATMEAL.LIGHTER],
  link: [theme.colors.OPACITY.OATMEAL.TRANSPARENT, theme.colors.OPACITY.OATMEAL.TRANSPARENT],
};

const HOVER_OPACITIES: Record<Palette, number[]> = {
  primary: [1, 1],
  outline: [1, 1],
  link: [1, 0.8],
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  touchable: {
    borderRadius: theme.radius['3xLarge'],
  },
  primary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['1'],
    height: 64,
    paddingHorizontal: theme.spacing['8'],
    overflow: 'hidden',
    borderRadius: theme.radius['3xLarge'],
  },
  primaryLabel: {
    color: theme.colors.DARK_KALE,
  },
  outline: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 64,
    paddingHorizontal: theme.spacing['8'],
    borderRadius: theme.radius['3xLarge'],
    borderWidth: 1,
    borderColor: theme.colors.WHITE,
  },
  outlineLabel: {
    color: theme.colors.WHITE,
  },
  link: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkLabel: {
    color: theme.colors.OATMEAL,
  },
});

const buttonPaletteStyles: Record<Palette, ViewStyle> = {
  primary: styles.primary,
  outline: styles.outline,
  link: styles.link,
};

const labelPaletteStyles: Record<Palette, TextStyle> = {
  primary: styles.primaryLabel,
  outline: styles.outlineLabel,
  link: styles.linkLabel,
};

const TOUCHABLE_WEB_STYLES = webOnlyStyles({
  outlineColor: theme.colors.OATMEAL,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  palette: Palette;
  label: string;
  icon?: IconName;
  disabled?: boolean;
  onPress: () => void;
}>;

type Palette = 'primary' | 'outline' | 'link';
